'use client'

import { ProductCard } from '@/feature/product/components/product-card/ProductCard.client'
import { useVisibleProductCards } from '@/feature/product/hooks/useVisibleProductCards'
import { pageListAnalyticsAtom } from '@/feature/product/services/product.store'
import { getProductClickContext } from '@/feature/product/utils/productListAnalytics'

import { useProductsListFavoriteStatus } from '@/feature/product/hooks/useProductsListFavoriteStatus'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  type ProductCardFragment,
  ProductListPriceAndStockDocument,
  type ProductListProductsWithoutPriceFragment,
  ProductType,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { handleErrors } from '@/lib/graphql/apiErrors'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { InfiniteScroll } from '@/ui/infinite-scroll/InfiniteScroll'
import { getProductListSource } from '../../services/productList.analytics'
import { EMBED_PRODUCTS_DISPLAYED_PER_PAGE } from '../../services/productList.constant'
import type {
  ProductListProductsQueryData,
  QueryKey,
  QueryProductsProductsList,
} from '../../services/productList.types'

import {
  getProductListByQueryKey,
  getQueryDocumentByQueryKey,
} from '../../services/productList.utils'
import { createProductListLoadingErrorIntent } from './services/productList.intents'

type EmbedProductListProductsProps = {
  productList: ProductListProductsWithoutPriceFragment[]
  queryKey: QueryKey
  queryProducts: QueryProductsProductsList
  totalPages: number
  maxNumberOfProducts: number
}

export default function EmbedProductListProducts({
  productList,
  queryKey,
  queryProducts,
  maxNumberOfProducts,
}: EmbedProductListProductsProps) {
  const { t } = useTranslation(['productListSearchPage'])

  const totalPages = Math.ceil(maxNumberOfProducts / EMBED_PRODUCTS_DISPLAYED_PER_PAGE)
  const initialPageIndex = Math.ceil(productList.length / EMBED_PRODUCTS_DISPLAYED_PER_PAGE)

  const [pageIndex, setPageIndex] = useState(initialPageIndex)

  // Setting a local state with the data initially loaded on the server
  const [listItems, setListItems] = useState(productList)

  const initialReferences = useMemo(() => productList.map((p) => p.reference), [productList])

  // Keep track of reference for which price and stocks have already been fetched
  const [fetchedReferences, setFetchedReferences] = useState(() => new Set(initialReferences))

  // Saving new references to fetch their price and stock after the new products are loaded
  const [pendingProductRefs, setPendingProductRefs] = useState<string[]>([])

  // Fetching P&S for the initial products loaded on the server
  const { data: initialPricesAndStocks, loading: initialLoading } = useQuery(
    ProductListPriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { references: initialReferences },
    },
  )

  // Fetching P&S for the new products loaded on scroll
  const { data: newPricesAndStocks, loading: stocksLoading } = useQuery(
    ProductListPriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { references: pendingProductRefs },
      skip: pendingProductRefs.length === 0,
    },
  )

  // Accumulating P&S results to avoid losing the previous ones when new are loaded
  const [accumulatedPricesAndStocks, setAccumulatedPricesAndStocks] = useState(
    initialPricesAndStocks?.productsByReferences ?? [],
  )

  // Initial price loading
  const isInitialized = useRef(false)

  useEffect(() => {
    if (initialPricesAndStocks?.productsByReferences && !isInitialized.current) {
      setAccumulatedPricesAndStocks(initialPricesAndStocks.productsByReferences)
      isInitialized.current = true
    }
  }, [initialPricesAndStocks])

  // Load price on new items loaded
  useEffect(() => {
    if (newPricesAndStocks?.productsByReferences?.length) {
      setAccumulatedPricesAndStocks((prev) => [
        ...prev,
        ...(newPricesAndStocks.productsByReferences ?? []),
      ])
    }
  }, [newPricesAndStocks])

  const [fetchMoreItems] = useLazyQuery<ProductListProductsQueryData>(
    getQueryDocumentByQueryKey(queryKey),
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
    },
  )

  // We need to keep track of article references separately to fetch favorites status, and only add new ones to the list to avoid unnecessary calls to the favorites endpoint
  const [accumulatedArticleReferences, setAccumulatedArticleReferences] = useState(() =>
    productList.filter((p) => p.type === ProductType.Article).map((p) => p.reference),
  )

  const handleFetchMore = useCallback(async () => {
    if (queryProducts.variables) {
      const nextPageIndex = pageIndex + 1

      const { data, error } = await fetchMoreItems({
        variables: {
          ...queryProducts.variables,
          pageIndex: nextPageIndex,
          pageSize: EMBED_PRODUCTS_DISPLAYED_PER_PAGE,
        },
      })

      if (error) {
        return handleErrors(error.graphQLErrors, {
          onError: {
            default: () => {
              console.error('Error fetching more products:', error)
              handleIntent(createProductListLoadingErrorIntent(error.message))
            },
          },
        })
      }

      if (data) {
        const newProducts = getProductListByQueryKey(data, queryKey)?.products || []

        setListItems((prevListItems) => {
          const existingUrls = new Set(prevListItems.map((p) => p.urlKey))
          const deduplicated = newProducts.filter((p) => !existingUrls.has(p.urlKey))

          return [...prevListItems, ...deduplicated]
        })

        // Keep only the new references to fetch their price and stock, and avoid fetching again for the previous ones
        const newReferences = newProducts
          .map((p) => p.reference)
          .filter((ref) => !fetchedReferences.has(ref))

        // Trigger the P&S query for the new references only
        setPendingProductRefs(newReferences)

        // Mark these references as fetched so they won't be queried again on next scrolls
        setFetchedReferences((prev) => new Set([...prev, ...newReferences]))
        const newArticleReferences = newProducts
          .filter((p) => p.type === ProductType.Article && !fetchedReferences.has(p.reference))
          .map((p) => p.reference)

        if (newArticleReferences.length > 0) {
          // Append new article references to trigger the favorites query for new articles only
          setAccumulatedArticleReferences((prev) => [...prev, ...newArticleReferences])
        }

        setPageIndex(nextPageIndex)
      }
    }
  }, [fetchMoreItems, queryKey, queryProducts.variables, pageIndex, fetchedReferences])

  const { data: favoritesMap } = useProductsListFavoriteStatus(accumulatedArticleReferences)

  const pageListAnalytics = useAtomValue(pageListAnalyticsAtom)
  const searchParams = useSearchParams()

  const productCardsRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])

  const productClickContext = getProductClickContext(
    queryKey,
    !!searchParams.get('filter'),
    pageListAnalytics.title,
  )

  const { setProductCardsRefs } = useVisibleProductCards({
    itemListName: productClickContext,
  })

  useEffect(() => {
    productCardsRefs.current = listItems.map(
      (_, index) => productCardsRefs.current[index] ?? { current: null },
    )
    setProductCardsRefs([...productCardsRefs.current])
  }, [listItems, setProductCardsRefs])

  const productCards = listItems.map((product, index) => (
    <ProductCard
      ref={productCardsRefs.current[index]}
      key={product.urlKey}
      product={product as ProductCardFragment}
      productPriceAndStock={{
        priceAndStockData: accumulatedPricesAndStocks?.find(
          (priceAndStock) => priceAndStock.reference === product.reference,
        ),
        priceAndStockIsLoading:
          initialLoading || (stocksLoading && pendingProductRefs.includes(product.reference)),
      }}
      isFavorite={!!favoritesMap?.[product.reference]}
      responsive
      position={index + 1}
      source={getProductListSource({
        searchParams,
        pageListAnalytics,
        queryKey,
      })}
    />
  ))

  return (
    <InfiniteScroll
      dataLength={listItems.length}
      next={handleFetchMore}
      hasMore={pageIndex < totalPages}
      className="product-list"
      endMessage={
        <p className="flex justify-center p-2">
          <b>{t('productListSearchPage:infiniteScroll.endMessage')}</b>
        </p>
      }
    >
      <div className="product-list-grid gap-2">{productCards}</div>
    </InfiniteScroll>
  )
}
